import { supabase } from "../lib/supabaseClient";
import { toast } from "src/hooks/use-toast";

export interface Inquiry {
  id: string;
  created_at: string;
  name: string;
  email: string;
  phone: string | null;
  inquiry_type: string;
  message: string;
  status: string;
}

// Inquiry service functions for React Query
export const inquiryService = {
  // Fetch all inquiries with optional limit
  fetchInquiries: async (limit?: number): Promise<Inquiry[]> => {
    let query = supabase
      .from("inquiries")
      .select("*")
      .order("created_at", { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching inquiries:", error);
      throw new Error("Failed to fetch inquiries");
    }

    return data || [];
  },

  // Fetch only new inquiries with optional limit
  fetchNewInquiries: async (limit?: number): Promise<Inquiry[]> => {
    let query = supabase
      .from("inquiries")
      .select("*")
      .eq("status", "new")
      .order("created_at", { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching new inquiries:", error);
      throw new Error("Failed to fetch new inquiries");
    }

    return data || [];
  },

  // Update inquiry status
  updateInquiryStatus: async (id: string, status: string): Promise<Inquiry> => {
    const { data, error } = await supabase
      .from("inquiries")
      .update({ status })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating inquiry status:", error);
      throw new Error("Failed to update inquiry status");
    }

    return data;
  },

  // Delete inquiry
  deleteInquiry: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("inquiries")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting inquiry:", error);
      throw new Error("Failed to delete inquiry");
    }
  },
};

export const deleteInquiry = async (
  id: string,
  inquiries: Inquiry[],
  setInquiries: React.Dispatch<React.SetStateAction<Inquiry[]>>,
  setNewCount?: React.Dispatch<React.SetStateAction<number>>
) => {
  try {
    const { error } = await supabase.from("inquiries").delete().eq("id", id);

    if (error) throw error;

    // Update local state
    setInquiries(inquiries.filter((inquiry) => inquiry.id !== id));

    // Update new count if needed
    if (setNewCount) {
      const wasNew = inquiries.find((i) => i.id === id)?.status === "new";
      if (wasNew) {
        setNewCount((prev) => prev - 1);
      }
    }

    toast({
      title: "Inquiry Deleted",
      description: "The inquiry has been permanently removed",
    });

    return true;
  } catch (error: any) {
    console.error("Error deleting inquiry:", error);
    toast({
      title: "Error",
      description: "Failed to delete inquiry",
      variant: "destructive",
    });
    return false;
  }
};
